import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import Logo from "../assets/logo1.png";
import generateCatalog from "../utils/generateCatalog";

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleCatalogClick = async (e) => {
    e.preventDefault();
    if (isGeneratingPDF) return;

    try {
      setIsGeneratingPDF(true);
      console.log('Starting catalog generation...');
      const success = await generateCatalog();

      if (success) {
        console.log('Catalog generated successfully');
      } else {
        console.error('Failed to generate catalog - returned false');
        alert('Could not generate the catalog. Please check the console for more details.');
      }
    } catch (error) {
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      alert(`Error generating PDF: ${error.message}`);
    } finally {
      setIsGeneratingPDF(false);
      closeMenu();
    }
  };

  return (
    <header
      className={`fixed w-full top-0 z-[40] font-poppins transition-all duration-500 ${isScrolled
          ? 'bg-white shadow-lg py-1'
          : 'bg-white py-4'
        }`}
    >
      {/* Main Header */}
      <div className="relative border-b border-slate-200">
        <div className="container mx-auto">
          <div className="flex justify-between items-center">
            {/* Logo Section */}
            <Link to="/" className="flex items-center">
              <div className="relative group">
                <div className="relative flex flex-col items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src={Logo}
                      alt="Triveni Industrial Gases Logo"
                      className={`object-contain transform transition-all duration-700 ${isScrolled ? 'w-24 h-24 group-hover:scale-105' : 'w-40 h-40 group-hover:scale-110'
                        }`}
                    />
                  </div>
                </div>
              </div>
            </Link>

            {/* Navigation - Desktop */}
            <nav className={`hidden lg:flex items-center space-x-8 transition-all duration-500 ${isScrolled ? 'scale-90 opacity-90' : 'scale-100 opacity-100'
              }`}>
              {[
                { name: "Home", path: "/" },
                { name: "About Us", path: "/about" },
                {
                  name: "Products",
                  path: "",
                  submenu: [
                    { name: "Welding Materials", path: "/products/welding-materials" },
                    { name: "Industrial Gas Cylinders", path: "/products/gas-cylinders" },
                    { name: "Ropes, Clamps & Web-slings", path: "/products/ropes-clamps" },
                    { name: "Personal Protective Equipment", path: "/products/ppe" },
                    { name: "Road & Fire Safety", path: "/products/safety" }
                  ]
                },
                {
                  name: isGeneratingPDF ? "Generating..." : "E-Catalogue",
                  path: "#",
                  onClick: handleCatalogClick
                },
                { name: "Contact Us", path: "/contact" }
              ].map((item, index) => (
                <div
                  key={item.name}
                  className="relative group"
                  style={{
                    animation: `fadeIn 0.5s ease-out ${index * 0.1}s both`
                  }}
                >
                  {item.onClick ? (
                    <button
                      onClick={item.onClick}
                      disabled={isGeneratingPDF}
                      className="group relative py-2"
                    >
                      <span className={`relative z-10 text-slate-700 text-lg font-medium group-hover:text-[#D82F2F] transition-colors duration-300 ${isGeneratingPDF ? 'opacity-50' : ''}`}>
                        {item.name}
                      </span>
                      <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-[#D82F2F] group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
                    </button>
                  ) : (
                    <Link
                      to={item.path}
                      className="group relative py-2"
                    >
                      <span className={`relative z-10 text-slate-700 text-lg font-medium group-hover:text-[#D82F2F] transition-colors duration-300 ${isScrolled ? 'text-base' : 'text-lg'
                        }`}>
                        {item.name}
                      </span>
                      <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-[#D82F2F] group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
                    </Link>
                  )}

                  {/* Submenu for Products */}
                  {item.submenu && (
                    <div className="absolute left-0 mt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                      <div className="relative bg-white rounded-lg shadow-lg border border-slate-200">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.path}
                            className="block px-4 py-3 text-slate-600 hover:text-[#D82F2F] hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg transition-colors duration-300"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="lg:hidden relative group"
            >
              <span className="relative p-2 bg-white rounded-lg block border border-slate-200 shadow-sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-slate-700 group-hover:text-[#D82F2F] transition-colors duration-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Portal */}
      {isMenuOpen && createPortal(
        <div className="fixed inset-0 z-[999999]">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-white/95 backdrop-blur-xl">
            <div className="absolute inset-0 bg-grid-slate-100 bg-[size:60px_60px]"></div>
          </div>

          {/* Content */}
          <div className="fixed inset-0 overflow-y-auto">
            <div className="min-h-full flex flex-col">
              {/* Mobile Menu Header */}
              <div className="sticky top-0 bg-white/80 backdrop-blur-sm border-b border-slate-200">
                <div className="flex justify-end items-center p-6">
                  <button
                    onClick={closeMenu}
                    className="relative group"
                  >
                    <span className="relative p-2 bg-white rounded-lg block border border-slate-200 shadow-sm">
                      <svg
                        className="w-6 h-6 text-slate-700 group-hover:text-[#D82F2F] transition-colors duration-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>

              {/* Mobile Menu Navigation */}
              <div className="flex-1 p-6">
                <nav>
                  <ul className="space-y-6">
                    {[
                      { name: "Home", path: "/" },
                      { name: "About Us", path: "/about" },
                      {
                        name: "Products",
                        path: "",
                        submenu: [
                          { name: "Welding Materials", path: "/products/welding-materials" },
                          { name: "Industrial Gas Cylinders", path: "/products/gas-cylinders" },
                          { name: "Ropes, Clamps & Web-slings", path: "/products/ropes-clamps" },
                          { name: "Personal Protective Equipment", path: "/products/ppe" },
                          { name: "Road & Fire Safety", path: "/products/safety" }
                        ]
                      },
                      {
                        name: isGeneratingPDF ? "Generating..." : "E-Catalogue",
                        path: "#",
                        onClick: handleCatalogClick
                      },
                      { name: "Contact Us", path: "/contact" }
                    ].map((item) => (
                      <li key={item.name}>
                        <div className="space-y-2">
                          {item.onClick ? (
                            <button
                              onClick={item.onClick}
                              disabled={isGeneratingPDF}
                              className={`group flex items-center justify-between text-slate-700 hover:text-[#D82F2F] w-full ${isGeneratingPDF ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <span className="text-2xl font-medium group-hover:translate-x-2 transition-transform duration-300">
                                {item.name}
                              </span>
                            </button>
                          ) : (
                            <Link
                              to={item.path}
                              className="group flex items-center justify-between text-slate-700 hover:text-[#D82F2F]"
                              onClick={item.submenu ? undefined : closeMenu}
                            >
                              <span className="text-2xl font-medium group-hover:translate-x-2 transition-transform duration-300">
                                {item.name}
                              </span>
                              {item.submenu && (
                                <svg className="w-5 h-5 text-[#D82F2F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                              )}
                            </Link>
                          )}
                          {item.submenu && (
                            <div className="pl-4 space-y-2 border-l border-slate-200">
                              {item.submenu.map((subItem) => (
                                <Link
                                  key={subItem.name}
                                  to={subItem.path}
                                  className="block text-slate-600 hover:text-[#D82F2F] py-2 text-lg transition-colors duration-300"
                                  onClick={closeMenu}
                                >
                                  {subItem.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}

export default Header;